/**
 * Nox: Zwei kleine Zugaben für die Artikelseite.
 *
 * 1. Das Inhaltsverzeichnis markiert den Abschnitt, der gerade im Bild ist.
 * 2. Der Knopf "Link" kopiert die Adresse des Beitrags in die Zwischenablage.
 *
 * Beides ist Zugabe, keine Voraussetzung: Ohne dieses Skript bleiben die
 * Sprungmarken des Verzeichnisses und der Mail-Link voll bedienbar, der
 * Kopieren-Knopf bleibt schlicht ausgeblendet (er steht im Liquid auf
 * `hidden` und wird erst hier eingeblendet).
 *
 * Kein Framework, keine Abhängigkeit, kein Polling — ein IntersectionObserver
 * und ein Klick-Listener.
 */

const TOC_LINKS = '.nox-article-toc__link';
const ACTIVE_CLASS = 'nox-article-toc__link--current';
const COPY_BUTTON = '[data-nox-copy-link]';

/**
 * Hebt im Inhaltsverzeichnis den Abschnitt hervor, der gerade sichtbar ist.
 *
 * Das Beobachtungsfenster ist bewusst auf das obere Drittel beschränkt
 * (`rootMargin`). Sonst wäre beim Scrollen fast immer mehr als eine
 * Überschrift im Bild und die Markierung würde springen.
 */
function markiereSichtbarenAbschnitt() {
  const links = Array.from(document.querySelectorAll(TOC_LINKS));
  if (links.length === 0) return;

  /** @type {Map<string, HTMLAnchorElement>} */
  const nachId = new Map();
  /** @type {Element[]} */
  const ueberschriften = [];

  for (const link of links) {
    const id = decodeURIComponent(link.getAttribute('href') || '').slice(1);
    const ziel = id ? document.getElementById(id) : null;
    if (!ziel) continue;
    nachId.set(id, link);
    ueberschriften.push(ziel);
  }

  if (ueberschriften.length === 0) return;

  let aktuell = null;

  const setze = (id) => {
    if (id === aktuell) return;
    aktuell = id;
    for (const [key, link] of nachId) {
      link.classList.toggle(ACTIVE_CLASS, key === id);
    }
  };

  const beobachter = new IntersectionObserver(
    (eintraege) => {
      const sichtbar = eintraege
        .filter((eintrag) => eintrag.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (sichtbar.length > 0) {
        setze(sichtbar[0].target.id);
        return;
      }

      // Nichts im Fenster: die letzte Überschrift oberhalb gilt weiter.
      const oberhalb = ueberschriften.filter(
        (kopf) => kopf.getBoundingClientRect().top < 0
      );
      if (oberhalb.length > 0) setze(oberhalb[oberhalb.length - 1].id);
    },
    { rootMargin: '0px 0px -66% 0px', threshold: 0 }
  );

  for (const kopf of ueberschriften) beobachter.observe(kopf);
}

/**
 * Blendet den Kopieren-Knopf ein und legt das Kopieren darauf.
 *
 * Die Rückmeldung ersetzt die Beschriftung für zwei Sekunden und wird über
 * `aria-live` auch vorgelesen. Scheitert das Kopieren — etwa ohne sicheren
 * Kontext —, bleibt die alte Beschriftung stehen; es wird nichts Falsches
 * behauptet.
 */
function ruesteKopierenNach() {
  const knopf = document.querySelector(COPY_BUTTON);
  if (!knopf) return;
  if (!navigator.clipboard) return;

  knopf.hidden = false;
  knopf.setAttribute('aria-live', 'polite');

  const beschriftung = knopf.textContent;
  const fertig = knopf.dataset.noxCopyDone || beschriftung;
  const adresse = knopf.dataset.noxCopyUrl || window.location.href;
  let zeitgeber = null;

  knopf.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(adresse);
    } catch {
      return;
    }

    knopf.textContent = fertig;
    if (zeitgeber) clearTimeout(zeitgeber);
    zeitgeber = setTimeout(() => {
      knopf.textContent = beschriftung;
    }, 2000);
  });
}

markiereSichtbarenAbschnitt();
ruesteKopierenNach();
